import {
  validateRequired,
  validateString,
  validationObject,
  validateId,
  validatePassCode,
  validateRequiredPhoneNumber,
} from 'utils/validations';
import { AssetInfo, EventInfo } from 'utils/constants/informationText';
import {
  AssetPlaceholder,
  BASE_ASSET_CATEGORY,
} from 'utils/constants/assetText';
import { getFormInitialValues, optionalFormField } from 'utils/methods/helpers';
import { ASSET_TYPE_FIELDS } from 'utils/formFields/assetType';
import isEmpty from 'lodash/isEmpty';
import { USER_FIELDS } from 'utils/formFields/user';
import AssetTypeForm from 'containers/Pages/Assets/AssetTypes/form';
import API from 'services/api';
import { setSiteName } from 'utils/helper/site';
import { transform as siteListTransform } from 'utils/DataTransform/Sites/SitesList';
import { operatorOptions } from '../helper';
import { getEventCategoryByServices } from 'containers/Pages/Events/Layouts';
import { v4 } from 'uuid';

export const validationSchema = (assetCategory, editMode, imageRequired) => {
  return validationObject({
    category: validateRequired(),
    assetName: validateString(30, true),
    type: validateString(30, true),
    assetId: checkAssetForRegistration(assetCategory)
      ? validateString(30, true)
      : validateString(30, false),
    ...(imageRequired && { image: validateRequired() }),
    ...(assetCategory === BASE_ASSET_CATEGORY.PHONE.id && {
      phone: validateRequiredPhoneNumber(),
    }),
    ...((assetCategory === BASE_ASSET_CATEGORY.SENSOR.id ||
      assetCategory === BASE_ASSET_CATEGORY.ROBOT.id) && {
      password: validatePassCode(!editMode, 4, 6),
    }),
    assetTypeId: validateId({
      maxLength: 30,
      required: false,
      allowedNull: true,
    }),
  });
};

export const initialState = {
  assetId: undefined,
  category: '',
  type: '',
  archived: false,
  licenseRequired: false,
  assetName: undefined,
  assetTypeId: null,
  qualificationRequired: false,
  handoverRequired: false,
  newAssetType: false,
};
export const initialValues = data => {
  const initialValues = getFormInitialValues(data);
  return {
    ...initialValues,
    ...(!isEmpty(data) &&
      data.others &&
      data.others.image && { image: data.others.image.value }),
  };
};

export const getSelectAssetFieldValue = (
  assetTypeList,
  assetCategory,
  type,
  field,
  // eslint-disable-next-line
) => {
  const selectedValue = assetTypeList.find(
    assetType =>
      assetType.category === assetCategory && assetType.name === type.typeName,
  );
  if (selectedValue) {
    return selectedValue[field];
  } else {
    return '-';
  }
};

export const getBaseFields = ({
  assetCategory,
  userAssignedDepartments = [],
  showDepartments,
  securityCompanyId,
  selectedDepartments,
  allowableCategories,
}) => [
  ASSET_TYPE_FIELDS.category({
    allowableCategories,
    size: 'half',
  }),
  {
    field: 'type',
    type: 'async-select',
    required: true,
    disabled: !assetCategory,
    fetchOptions: API.getAssetTypeList,
    fetchFilters: {
      securityCompanyId,
      archived: false,
      category: assetCategory,
    },
    optionLabelKey: 'typeName',
    size: 'half',
    dataTransform: ({ data }) => {
      return data.map(m => ({
        id: m.id,
        typeName: m.name,
        qualificationRequired: m.qualificationRequired,
        handoverRequired: m.handoverRequired,
      }));
    },
    resetWhen: [
      {
        value: assetCategory,
        condition: val => {
          return val;
        },
      },
    ],
    // info: AssetInfo.types,
  },
  {
    field: 'type', // this field name should be same as the one you are trying to replace value with
    type: 'embedded-form-follow-up',
    size: 'full',
    label: 'New Asset Type',
    component: AssetTypeForm,
    archiveDelete: true,
    hideTable: true,
    addAllValues: true,
    componentProps: {
      embeddedForm: true,
      disablePresist: true,
      embeddedAssetCategory: assetCategory,
      enableEmbeddedAssetCategory: assetCategory === '', // always disable asset catgeory selection if asset types are created from within asset form
    },
  },
  ...assetNameAndIdField(assetCategory),
  {
    field: 'site',
    type: 'async-select',
    required: false,
    disabled: false,
    size: 'half',
    valueType: 'object',
    fetchOptions: API.fetchSitesList,
    fetchFilters: {
      securityCompanyId,
      archived: false,
      ...(!isEmpty(selectedDepartments) && {
        departmentId: selectedDepartments
          .map(m => ({ id: m.id }))
          .map(m => m.id),
      }),
    },
    optionLabelKey: 'label', // make sure we dont replace the actual api key with the custom name here
    resetWhen: [
      {
        value: selectedDepartments,
        condition: val => {
          return val;
        },
      },
    ],
    dataTransform: ({ data }) => {
      const transformedNameSites = data.map(site => ({
        ...site,
        id: site.id,
        label: setSiteName(site),
      }));
      return siteListTransform(transformedNameSites, userAssignedDepartments);
    },
  },
  ...optionalFormField({
    showWhen: showDepartments,
    field: USER_FIELDS.departments({ userAssignedDepartments }),
  }),
];
export const assetNameAndIdField = assetCategory => [
  {
    field: 'assetId',
    label:
      assetCategory === BASE_ASSET_CATEGORY.VEHICLE.id
        ? 'Registration'
        : 'Asset ID',
    type: 'text',
    required: assetCategory === BASE_ASSET_CATEGORY.VEHICLE.id,
    placeholder: AssetPlaceholder.assetId,
    size: 'half',
    info: AssetInfo.assetId,
  },
  {
    field: 'assetName',
    type: 'text',
    required: true,
    placeholder: AssetPlaceholder.assetName,
    size: 'half',
  },
];
const checkAssetForRegistration = assetCategory => {
  const registrationCategoryAsset = [
    BASE_ASSET_CATEGORY.VEHICLE.id,
    BASE_ASSET_CATEGORY.VEHICLE_BUS.id,
    BASE_ASSET_CATEGORY.VEHICLE_OFF_ROAD.id,
    BASE_ASSET_CATEGORY.VEHICLE_VAN.id,
  ];
  return registrationCategoryAsset.includes(assetCategory);
};
export const generalSection = ({
  assetCategory,
  editMode,
  countries,
  assetTypeList,
  // type,
  // hideAssetTypeSubPanel,
  allowableCategories,
  userAssignedDepartments = [],
  securityCompanyId,
  selectedDepartments = [],
}) => {
  const showDepartments = userAssignedDepartments.length > 0;
  const baseFields = getBaseFields({
    assetTypeList,
    assetCategory,
    allowableCategories,
    userAssignedDepartments,
    showDepartments,
    securityCompanyId,
    departmentIds: selectedDepartments.map(m => ({ id: m.id })).map(i => i.id),
    selectedDepartments,
  });

  switch (assetCategory) {
    case BASE_ASSET_CATEGORY.PHONE.id:
      return [
        ...baseFields,
        {
          field: 'phone',
          type: 'phone',
          required: true,
          size: 'half',
          secondaryOptions: countries,
        },
      ];
    case BASE_ASSET_CATEGORY.ROBOT.id:
    case BASE_ASSET_CATEGORY.SENSOR.id:
      return [
        ...baseFields,
        {
          field: 'password',
          label: 'Passcode',
          type: 'text',
          size: 'half',
          required: !editMode,
          placeholder: 'Enter 4-6 digit passcode. e.g. 4603',
        },
      ];
    default:
      return baseFields;
  }
};

export const detailedSection = ({
  assetCategory,
  //eventCategory,
  type,
  services,
}) => {
  switch (assetCategory) {
    case BASE_ASSET_CATEGORY.VEHICLE.id:
    case BASE_ASSET_CATEGORY.CANINE.id:
    case BASE_ASSET_CATEGORY.FIREARM.id:
    case BASE_ASSET_CATEGORY.VEHICLE_OFF_ROAD.id:
    case BASE_ASSET_CATEGORY.VEHICLE_VAN.id:
    case BASE_ASSET_CATEGORY.VEHICLE_BUS.id:
    case BASE_ASSET_CATEGORY.OTHER.id:
      return [
        {
          field: 'qualificationRequired',
          label: 'Qualification Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.qualificationRequired,
          info: AssetInfo.qualifications,
        },
        {
          field: 'handOverRequired',
          label: 'Handover Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.handoverRequired,
          info: AssetInfo.handOver,
        },
        {
          field: 'licenseRequired',
          type: 'toggle',
          required: true,
          size: 'half',
        },
        {
          field: 'description',
          type: 'textArea',
          maxLength: 250,
        },
      ];
    case BASE_ASSET_CATEGORY.PHONE.id:
      return [
        {
          field: 'qualificationRequired',
          label: 'Qualification Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.qualificationRequired,
          info: AssetInfo.qualifications,
        },
        {
          field: 'handOverRequired',
          label: 'Handover Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.handoverRequired,
          info: AssetInfo.handOver,
        },
        {
          field: 'description',
          type: 'textArea',
          maxLength: 250,
        },
      ];
    case BASE_ASSET_CATEGORY.ROBOT.id:
    case BASE_ASSET_CATEGORY.SENSOR.id:
      return [
        {
          field: 'qualificationRequired',
          label: 'Qualification Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.qualificationRequired,
          info: AssetInfo.qualifications,
        },
        {
          field: 'handOverRequired',
          label: 'Handover Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.handoverRequired,
          info: AssetInfo.handOver,
        },
        {
          field: 'rules',
          label: 'Data Rules',
          type: 'sub-form',
          formHeader: 'Add new rule',
          subFields: [
            {
              field: 'name',
              type: 'text',
              size: 'full',
              required: true,
            },
            {
              label: 'Key',
              field: 'key',
              type: 'text',
              size: 'third',
              required: true,
            },
            {
              field: 'operator',
              type: 'select',
              options: operatorOptions,
              size: 'third',
              required: true,
            },
            {
              label: 'Value',
              field: 'value',
              type: 'text',
              size: 'third',
              required: true,
            },
            {
              field: 'eventCategory',
              type: 'select',
              size: 'half',
              options: getEventCategoryByServices(services),
              required: true,
              info: EventInfo.category,
            },
            {
              // In sub-form, filterOptions and disabled are defined in sub-form component
              field: 'eventType',
              type: 'async-select',
              size: 'half',
              fetchOptions: API.fetchEventTypes,
              optionLabelKey: 'typeName',
              dataTransform: ({ data }) => {
                return data.map(m => ({
                  ...m,
                  id: m.id !== '' ? m.id : v4(), // assign the id as the static categories does not have id
                }));
              },
              required: true,
              // info: EventInfo.types,
            },
            {
              field: 'duration',
              type: 'text',
              size: 'full',
            },
          ],
          defaultValues: {
            name: '',
            key: '',
            operator: 'EQUAL',
            value: '',
            eventCategory: '',
            eventType: '',
            duration: 0,
          },
          requiredFields: [
            'eventCategory',
            'name',
            'key',
            'eventType',
            'operator',
            'value',
          ],
        },
        {
          field: 'description',
          type: 'textArea',
          maxLength: 250,
        },
      ];
    default:
      return [
        {
          field: 'qualificationRequired',
          label: 'Qualification Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.qualificationRequired,
          info: AssetInfo.qualifications,
        },
        {
          field: 'handOverRequired',
          label: 'Handover Required',
          type: 'bool-read-only',
          size: 'half',
          value: type?.handoverRequired,
          info: AssetInfo.handOver,
        },
        {
          field: 'description',
          type: 'textArea',
          maxLength: 250,
        },
      ];
  }
};
